import type {
	IOffsetVisibility,
	ISpreadingCompaction,
	ISpreadingCompactionInput,
	ITarget,
	ITerrain,
	TOffsetType,
} from "@/store/modules/spreading-compaction-types";
import { hasCompactionOutlineForOffsets } from "./exporters/surfaceExportFiles";

// --- Types ---

interface ITargetObjectLike {
	id: string | number;
	type: string;
	name?: string;
	isShow?: boolean;
}

interface IDesignLike {
	design_id: number;
	design_name: string;
	asset_id: string;
	getVisibility: () => boolean;
}

interface IPointCloudLike {
	point_cloud_id: number;
	point_cloud_name?: string;
	/** Same ID space as `associatedTerrain.id`, unlike `point_cloud_id`. */
	asset_id?: string;
	getIsAssetUploaded: () => string;
}

/** `id` is in the asset_id space, not the terrain's own primary key. */
interface IAssociatedTerrainLike {
	id: number;
	terrainType: string;
}

/** Tiling finished, so a height map exists. */
const ASSET_UPLOADED_DONE = "DONE";

export interface ISelectionItem {
	/** 表示用ラベル。種別名を含むためロケール依存 */
	label: string;
	value: string;
	/** 種別名を含まない素の名称。保存する `name` に使う */
	name: string;
}

/**
 * i18n キーを解決する関数。helpers を vue-i18n 非依存に保つため注入する。
 */
export type TTranslate = (key: string) => string;

// --- Constants ---

/** 面クリック時の BFS 許容角度 (度)。UI からは変更できない既定値 */
export const DEFAULT_ANGLE_TOLERANCE = 2;
export const MAX_ANGLE_TOLERANCE = 180;

/** ターゲット種別 → 辞書キー。ラベルの種別部分を翻訳するために使う */
const TARGET_TYPE_LABEL_KEYS = {
	road: "ROAD",
	flatground: "FLATGROUND",
	design: "DESIGN",
} as const satisfies Record<ITarget["type"], string>;

/** ターゲット種別の表示名を辞書から引く */
function targetTypeLabel(type: ITarget["type"], t: TTranslate): string {
	return t(TARGET_TYPE_LABEL_KEYS[type]);
}

// --- Selection builders ---

export function buildTargetItems(
	objects: ITargetObjectLike[],
	designs: IDesignLike[],
	t: TTranslate,
): ISelectionItem[] {
	const items: ISelectionItem[] = [];

	for (const obj of objects) {
		if ((obj.type === "road" || obj.type === "flat_ground") && obj.isShow) {
			const normalizedType =
				obj.type === "flat_ground" ? "flatground" : obj.type;
			const name = obj.name || String(obj.id);
			items.push({
				label: `${name} (${targetTypeLabel(normalizedType, t)})`,
				value: `${normalizedType}:${obj.id}`,
				name,
			});
		}
	}

	for (const design of designs) {
		if (design.getVisibility() && design.asset_id) {
			items.push({
				label: `${design.design_name} (${targetTypeLabel("design", t)})`,
				value: `design:${design.asset_id}`,
				name: design.design_name,
			});
		}
	}

	return items;
}

/**
 * `buildTargetItems` drops hidden objects, so a saved entry can have a selected
 * value that is absent from the candidates and renders as a raw `road:<id>`.
 */
export function withSelectedTargetItem(
	items: ReadonlyArray<ISelectionItem>,
	target: Readonly<Pick<ITarget, "type" | "id" | "name">> | null | undefined,
	t: TTranslate,
): ISelectionItem[] {
	if (!target) return [...items];
	const value = `${target.type}:${target.id}`;
	if (items.some((item) => item.value === value)) return [...items];
	const name = target.name || String(target.id);
	return [
		...items,
		{ label: `${name} (${targetTypeLabel(target.type, t)})`, value, name },
	];
}

/**
 * Not narrowed by target kind — the associated terrain is only an initial value.
 * Untiled point clouds are excluded because they have no height map yet.
 */
export function buildTerrainItems(
	pointClouds: ReadonlyArray<IPointCloudLike>,
): ISelectionItem[] {
	return pointClouds
		.filter((pc) => pc.getIsAssetUploaded() === ASSET_UPLOADED_DONE)
		.map((pc) => {
			const name = pc.point_cloud_name ?? `PointCloud ${pc.point_cloud_id}`;
			return { label: name, value: `pointcloud:${pc.point_cloud_id}`, name };
		});
}

/**
 * Crosses ID spaces: `associatedTerrain.id` is an asset_id while a
 * SpreadingCompaction terrain is keyed by point_cloud_id.
 * @returns null for non-point-cloud, unresolved, or untiled terrain
 */
export function resolveAssociatedTerrainValue(
	associatedTerrain: Readonly<IAssociatedTerrainLike> | undefined,
	pointClouds: ReadonlyArray<IPointCloudLike>,
): string | null {
	// design / reference-topology cannot be used as a terrain yet (ADR 0002)
	if (associatedTerrain?.terrainType !== "pointcloud") return null;

	const matched = pointClouds.find(
		(pc) =>
			pc.asset_id !== undefined &&
			Number.parseInt(pc.asset_id, 10) === associatedTerrain.id,
	);
	if (!matched || matched.getIsAssetUploaded() !== ASSET_UPLOADED_DONE) {
		return null;
	}
	return `pointcloud:${matched.point_cloud_id}`;
}

// --- Selection parsers ---

export function parseTargetSelection(
	value: string | null,
	items: ISelectionItem[],
): ITarget | null {
	if (!value) return null;

	const [type, id] = value.split(":");
	const name = items.find((item) => item.value === value)?.name ?? "";

	switch (type) {
		case "road":
			return { type, id, name };
		case "flatground":
			return { type, id, name };
		case "design":
			return { type, id: Number(id), name };
		default:
			return null;
	}
}

export function parseTerrainSelection(
	value: string | null,
	items: ISelectionItem[],
): ITerrain | null {
	if (!value) return null;

	const [type, id] = value.split(":");
	const name = items.find((item) => item.value === value)?.name ?? "";

	switch (type) {
		case "pointcloud":
			return { type, id: Number(id), name };
		default:
			return null;
	}
}

// --- Offset helpers ---

/**
 * 転圧 CSV を出せるか: 実行済みで、全転圧オフセットに外周列がある。
 * 転圧層が無い場合も true (欠けているものが無い)。
 */
export function hasCompactionOutline(
	spreadingCompaction: Readonly<ISpreadingCompaction> | null | undefined,
): boolean {
	if (spreadingCompaction?.execution.status !== "success") return false;
	return hasCompactionOutlineForOffsets(
		collectOffsets(spreadingCompaction.execution.result.surfaces, "compaction"),
		spreadingCompaction.execution.compactionOutline,
	);
}

export function collectOffsets(
	parts: ISpreadingCompactionInput["surfaces"],
	type: TOffsetType,
): number[] {
	return [
		...new Set(
			parts.filter((part) => part.type === type).map((part) => part.offset),
		),
	].sort((a, b) => a - b);
}

export function isOffsetVisible(
	spreadingCompaction: ISpreadingCompaction | null | undefined,
	type: TOffsetType,
	offset: number,
): boolean {
	if (!spreadingCompaction) return false;
	const found = spreadingCompaction.offsetVisibility.find(
		(o) => o.type === type && o.offset === offset,
	);
	return found?.visible ?? true;
}

export function buildOffsetInitialVisibility(
	parts: ISpreadingCompactionInput["surfaces"],
): IOffsetVisibility[] {
	return parts.map((part) => ({
		type: part.type,
		offset: part.offset,
		visible: part.type !== "spreading",
	}));
}
